import { Option, pipe, ReadonlyArray, Runtime } from "fp";
import { useHistory, useRouteMatch } from "react-router-dom";

import { Asset } from "~/Asset";
import { Company } from "~/Company";
import * as Data from "~/Data";
import * as Distance from "~/Distance";
import * as Driver from "~/Driver";
import { Drivers } from "~/Driver";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Route as GenericRoute } from "~/Route";
import { Time } from "~/Time";

import * as Request from "./Request";
import { NewBranch as Branch } from "~/Branch";

export * as Count from "./Count";
export * as Download from "./Download";
export * as Page from "./Page";
export * as Request from "./Request";
export * as Table from "./Table";

const startToValueOf = ({ interval }: Time.Interval) =>
  Time.DateTime.toValue(interval.start);

export type Unassigned = Data.Reference.Reference &
  Time.Interval &
  Distance.Distance &
  Asset & {
    readonly driver: Partial<Driver.Driver>;
    readonly location: { readonly start: string; readonly stop?: string };
    readonly branch?: Branch.Branch;
  };

export type UnassignedLogs = { readonly logs: readonly Unassigned[] };

export type RemoteLogs = {
  readonly unassignedLogs: Remote<Log.Unassigned.UnassignedLogs>;
};

export namespace Route {
  export const baseRoute = () => `/unassigned` as const;
  export const definition = () => `${baseRoute()}/:start/:stop?`;

  export const useDefault = () => GenericRoute.use(href(Interval.initial()));

  export const href = ({
    interval: { start, stop },
  }: Time.Interval.Stopped): string =>
    pipe(
      [start, stop],
      ReadonlyArray.map(Time.DateTime.toISOWithoutTime),
      ([start, stop]) => `${baseRoute()}/${start}/${stop}`
    );

  export const useRouteInterval = (): Time.Interval.Stopped => {
    const routeInterval = Option.toUndefined(
      pipe(
        useRouteMatch<{ readonly start?: string; readonly stop?: string }>(
          definition()
        ),
        Option.fromNullable,
        Option.chain(({ params: { start, stop } }) =>
          start && stop ? Option.some({ start, stop }) : Option.none
        ),
        Option.chain(({ start, stop }) =>
          Time.Interval.Stopped.fromDateStringsStartEndDay(start)(stop)
        )
      )
    );
    return routeInterval ?? Interval.initial();
  };

  export type DriverRouteIdentity = Time.DateTime & {
    readonly driver: Pick<Driver.Driver, "id" | "name">;
  };
  export const useRowRoute = () => {
    const href = (route: DriverRouteIdentity): string =>
      `${Driver.Page.Route.href(route)}/${Time.DateTime.toISOWithoutTime(
        route
      )}`;

    const history = useHistory();
    return (route?: DriverRouteIdentity) => {
      // eslint-disable-next-line functional/no-expression-statement
      route && history.push(href(route));
    };
  };
}

export const empty = (): UnassignedLogs => ({ logs: [] });

export const orEmpty = (unassignedLogs: Remote<UnassignedLogs>) =>
  pipe(Remote.toUndefined(unassignedLogs), (logs) => logs ?? empty());

export const use = ({
  interval,
}: Time.Interval.Stopped): Remote<UnassignedLogs> => {
  const { company } = Company.use();
  const [
    _preferencesLoading,
    branchPreferences,
  ] = Company.Branch.Branches.useBranchFiltersFromPreferences();

  const [
    _assetsFiltersLoading,
    assetIDs,
  ] = Company.Assets.Filter.useAssetFiltersFromPreferences();

  return Remote.Query.use(
    Request.Query.key({ company, interval, branchIDs: branchPreferences, assetIDs }),
    Request.Query.request({ company, interval, branchIDs: branchPreferences, assetIDs }),
    {
      staleTime: Time.Duration.minutes(10).duration,
      keepPreviousData: false,
    }
  );
};

export type UseWithLoadingResult = {
  readonly isLoading: boolean;
  readonly unassignedLogs?: readonly Unassigned[];
};

export const useWithLoading = ({ interval }: Time.Interval.Stopped) =>
  Remote.when<UnassignedLogs, UseWithLoadingResult>(use({ interval }), {
    loading: () => ({ isLoading: true, unassignedLogs: undefined }),
    error: () => ({ isLoading: false, unassignedLogs: undefined }),
    success: (data) => ({ isLoading: false, unassignedLogs: data.logs }),
  });

export const useOrEmpty = ({
  interval,
}: Time.Interval.Stopped): UnassignedLogs => orEmpty(use({ interval }));

export const isLoading = () => Remote.isLoading(use(Route.useRouteInterval()));

export const injectParing = ({
  log,
  pairings,
  drivers,
}: { readonly log: Unassigned } & Drivers & {
    readonly pairings: readonly Driver.Pairing.Pairing[] | undefined;
  }) => {
  const pairingMatches = pairings
    ? pairings.filter(
        (p) =>
          p.asset.id == log.asset.id &&
          Time.Interval.Stopped.collides(
            Time.Interval.when(log, {
              stopped: (stopped) => stopped,
              started: ({ interval: { start } }) => ({
                interval: { start, stop: Time.DateTime.now() },
              }),
            })
          )(
            Time.Interval.when(p, {
              stopped: (stopped) => stopped,
              started: ({ interval: { start } }) => ({
                interval: { start, stop: Time.DateTime.now() },
              }),
            })
          )
      )
    : undefined;
  if (pairingMatches && pairingMatches.length > 0) {
    const reverseSortedPairingMatches = [
      ...pairingMatches,
    ].sort((a: Driver.Pairing.Pairing, b: Driver.Pairing.Pairing) =>
      startToValueOf(a) > startToValueOf(b)
        ? -1
        : startToValueOf(a) < startToValueOf(b)
        ? 1
        : 0
    );

    const firstPairingMatchingAKnownDriver = reverseSortedPairingMatches.find(
      (m) => drivers.some((driver) => m.driver.id == driver.id)
    );

    const matchedDriver = drivers.find(
      (driver) => driver.id == firstPairingMatchingAKnownDriver?.driver.id
    );

    if (matchedDriver) {
      return {
        ...log,
        driver: {
          id: matchedDriver.id,
          name: {
            first: matchedDriver.name?.first,
            last: matchedDriver.name?.last,
          },
        },
      };
    }
  }
  return log;
};

export namespace Interval {
  export const initial = (
    now: Time.DateTime = Time.DateTime.toEndOfDay()
  ): Time.Interval.Stopped =>
    Time.Interval.Stopped.fromDateTimes(
      Time.DateTime.toStartOfDay({
        dateTime: now.dateTime - Time.Duration.days(7).duration,
      }),
      Time.DateTime.toEndOfDay({ dateTime: now.dateTime })
    );
}

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      start_date: Time.DateTime.Response.runtime(),
      end_date: Runtime.nullable(Time.DateTime.Response.runtime()),
      driver_tracker_log_id: Runtime.number(),
      asset: Asset.Response.runtime(),
      distance: Runtime.number(),
      start_location: Runtime.string(),
      end_location: Runtime.nullable(Runtime.string()),
      branch: Runtime.nullable(Branch.Response.runtime()),
    });

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): Unassigned => ({
    ...Time.Interval.Stopped.fromDateTimes(
      response.start_date,
      response.end_date ?? Time.DateTime.now()
    ),
    id: `${response.driver_tracker_log_id}`,
    driver: {
      id: undefined,
      name: {
        first: undefined,
        last: undefined,
      },
    },
    ...Asset.Response.decode(response.asset),
    distance: { miles: response.distance },
    location: {
      start: response.start_location,
      stop: response.end_location ?? undefined,
    },
    branch: response.branch
      ? Branch.Response.decode(response.branch)
      : undefined,
  });

  export namespace QueryResponse {
    export const runtime = () => Runtime.array(Response.runtime());

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): UnassignedLogs => ({
      logs: response.map(Response.decode),
    });
  }
}
